import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { Sparkles } from 'lucide-react';

import { ColumnHeader } from '@/components/table/column-header';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SimulationRunShipVisit } from '@/routes/(app)/(scenarios)/(simulation)/simulation.types';

type CountSourceKey = keyof Pick<
  SimulationRunShipVisit,
  'unload_source' | 'load_source'
>;

/** Highlights counts the model predicted; extrapolated counts render as-is. */
const CountCell = ({
  value,
  isPredicted,
}: {
  value: number;
  isPredicted: boolean;
}) => {
  const { t } = useTranslation();

  if (!isPredicted) return value;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className='inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-primary'>
          <Sparkles
            className='size-3'
            aria-label={t('shipVisits.table.predicted')}
          />
          {value}
        </span>
      </TooltipTrigger>
      <TooltipContent>{t('shipVisits.table.predicted')}</TooltipContent>
    </Tooltip>
  );
};

const renderCount = (
  { getValue, row }: CellContext<SimulationRunShipVisit, unknown>,
  sourceKey: CountSourceKey
) => (
  <CountCell
    value={getValue<number>()}
    isPredicted={row.original[sourceKey] === 'predicted'}
  />
);

export const useSimulationShipVisitsColumns = () => {
  const { t } = useTranslation();

  return useMemo<Array<ColumnDef<SimulationRunShipVisit>>>(
    () => [
      {
        accessorKey: 'shipping_line_id',
        header: t('shipVisits.table.shipping_line_id'),
      },
      {
        accessorKey: 'id',
        header: t('shipVisits.table.id'),
      },
      {
        accessorKey: 'ship_id',
        header: t('shipVisits.table.ship_id'),
      },
      {
        accessorKey: 'arrival_time',
        header: ({ column }) => (
          <ColumnHeader
            column={column}
            enableSorting={true}
            title={t('shipVisits.table.arrival_time')}
          />
        ),
      },
      {
        accessorKey: 'n_moves_discharge',
        header: t('shipVisits.table.n_moves_discharge'),
        cell: (context) => renderCount(context, 'unload_source'),
      },
      {
        accessorKey: 'n_moves_load',
        header: t('shipVisits.table.n_moves_load'),
        cell: (context) => renderCount(context, 'load_source'),
      },
      {
        accessorKey: 'n_regular_import_containers',
        header: t('shipVisits.table.n_regular_import_containers'),
        cell: (context) => renderCount(context, 'unload_source'),
      },
      {
        accessorKey: 'n_reefer_import_containers',
        header: t('shipVisits.table.n_reefer_import_containers'),
        cell: (context) => renderCount(context, 'unload_source'),
      },
      {
        accessorKey: 'n_regular_export_containers',
        header: t('shipVisits.table.n_regular_export_containers'),
        cell: (context) => renderCount(context, 'load_source'),
      },
      {
        accessorKey: 'n_reefer_export_containers',
        header: t('shipVisits.table.n_reefer_export_containers'),
        cell: (context) => renderCount(context, 'load_source'),
      },
      {
        accessorKey: 'n_regular_transshipment_containers',
        header: t('shipVisits.table.n_regular_transshipment_containers'),
        cell: (context) => renderCount(context, 'unload_source'),
      },
      {
        accessorKey: 'n_reefer_transshipment_containers',
        header: t('shipVisits.table.n_reefer_transshipment_containers'),
        cell: (context) => renderCount(context, 'unload_source'),
      },
    ],
    [t]
  );
};
